// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Compras/Relatorios/PerformanceFornecedores.tsx
//
//  Fase 3 · Iter 3.3 — Performance de Fornecedores (OTIF + score A/B/C/D).
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import {
  Award, CheckCircle2, Clock, DollarSign, Percent, Target, TrendingUp, XCircle,
} from 'lucide-react';
import { format, subMonths } from 'date-fns';
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';

import { usePerformanceFornecedores } from '@/features/compras/hooks/useRecebimento';
import type { PerformanceFornecedor } from '@/features/compras/types';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

const fmtPct = (v: number) => `${v.toFixed(1)}%`;
const fmtDias = (v: number) => `${v.toFixed(1)} d`;

const classColor = (c: string) =>
  c === 'A' ? 'bg-green-500/10 text-green-700 border-green-500/30'
 : c === 'B' ? 'bg-blue-500/10 text-blue-700 border-blue-500/30'
 : c === 'C' ? 'bg-amber-500/10 text-amber-700 border-amber-500/30'
 :             'bg-destructive/10 text-destructive border-destructive/30';

const PerformanceFornecedores = () => {
  const hoje = new Date();
  const [dataInicio, setDataInicio] = useState(format(subMonths(hoje, 12), 'yyyy-MM-dd'));
  const [dataFim,    setDataFim]    = useState(format(hoje, 'yyyy-MM-dd'));

  const { data, isLoading } = usePerformanceFornecedores({ dataInicio, dataFim });

  const kpis = data?.resumo;
  const top    = data?.top    ?? [];
  const bottom = data?.bottom ?? [];
  const mensal = data?.otifMensal ?? [];

  const chartMensal = mensal.map((m) => ({
    mes:      m.rotuloMes,
    otif:     m.otifPercent,
    leadTime: m.leadTimeMedioDias,
    pos:      m.totalPOs,
  }));

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Performance de Fornecedores · OTIF</h1>
        <p className="text-muted-foreground mt-1">
          OTIF = On-Time + In-Full. Avalia pontualidade, completude, lead time e divergência de custo
          dos fornecedores com base nas POs confirmadas e recebimentos.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label>Data inicial</Label>
            <Input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Data final</Label>
            <Input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      {isLoading || !kpis ? (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <KpiCard
            icon={<Target className="h-4 w-4 text-blue-500" />}
            label="OTIF global"
            valor={fmtPct(kpis.otifGlobalPercent)}
            subtitulo={`${kpis.totalPOs} PO${kpis.totalPOs === 1 ? '' : 's'}`}
          />
          <KpiCard
            icon={<CheckCircle2 className="h-4 w-4 text-green-500" />}
            label="On-Time"
            valor={fmtPct(kpis.onTimePercent)}
          />
          <KpiCard
            icon={<CheckCircle2 className="h-4 w-4 text-amber-500" />}
            label="In-Full"
            valor={fmtPct(kpis.inFullPercent)}
            subtitulo={`${kpis.pOsCompletadas} completadas`}
          />
          <KpiCard
            icon={<Clock className="h-4 w-4 text-purple-500" />}
            label="Lead time médio"
            valor={fmtDias(kpis.leadTimeMedioDias)}
          />
          <KpiCard
            icon={<Percent className="h-4 w-4 text-cyan-500" />}
            label="Divergência preço"
            valor={fmtPct(kpis.divergenciaCustoMediaPercent)}
          />
        </div>
      )}

      {/* Gráfico OTIF mensal */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4" /> OTIF mensal
          </CardTitle>
        </CardHeader>
        <CardContent className="h-80">
          {isLoading ? (
            <Skeleton className="h-full w-full" />
          ) : chartMensal.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
              Sem POs avaliáveis no período.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartMensal}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left"  tick={{ fontSize: 11 }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: number, n: string) =>
                  n === 'otif' ? `${v.toFixed(1)}%` : fmtDias(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line yAxisId="left"  type="monotone" dataKey="otif"     name="OTIF %"   stroke="#3b82f6" strokeWidth={2} />
                <Line yAxisId="right" type="monotone" dataKey="leadTime" name="Lead time" stroke="#a855f7" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Top + Bottom */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RankingCard
          titulo="Top fornecedores"
          icon={<Award className="h-4 w-4 text-green-600" />}
          lista={top}
          loading={isLoading}
        />
        <RankingCard
          titulo="Piores performances"
          icon={<XCircle className="h-4 w-4 text-destructive" />}
          lista={bottom}
          loading={isLoading}
        />
      </div>

      {/* Gráfico OTIF por fornecedor (top 10) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">OTIF por fornecedor (top 10)</CardTitle>
        </CardHeader>
        <CardContent className="h-80">
          {isLoading ? (
            <Skeleton className="h-full w-full" />
          ) : top.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
              Sem fornecedores com POs suficientes para ranking (mínimo 2).
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={top.map((f) => ({
                  nome:    (f.fornecedorNome ?? `#${f.fornecedorId}`).slice(0, 24),
                  otif:    f.otifPercent,
                  onTime:  f.onTimePercent,
                  inFull:  f.inFullPercent,
                }))}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="nome" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={80} />
                <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v: number) => `${v.toFixed(1)}%`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="onTime" name="On-Time" fill="#22c55e" />
                <Bar dataKey="inFull" name="In-Full" fill="#f59e0b" />
                <Bar dataKey="otif"   name="OTIF"    fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PerformanceFornecedores;

// ── RankingCard ──────────────────────────────────────────────────────────────

function RankingCard({
  titulo, icon, lista, loading,
}: {
  titulo:  string;
  icon:    React.ReactNode;
  lista:   PerformanceFornecedor[];
  loading: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          {icon} {titulo}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-32 w-full" />
        ) : lista.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sem dados suficientes.</p>
        ) : (
          <div className="rounded-md border overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fornecedor</TableHead>
                  <TableHead className="text-right">POs</TableHead>
                  <TableHead className="text-right">OTIF</TableHead>
                  <TableHead className="text-right">Lead</TableHead>
                  <TableHead className="text-right">Div.</TableHead>
                  <TableHead>Score</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lista.map((f) => (
                  <TableRow key={f.fornecedorId}>
                    <TableCell className="max-w-[240px] truncate text-sm" title={f.fornecedorNome ?? ''}>
                      {f.fornecedorNome ?? `#${f.fornecedorId}`}
                    </TableCell>
                    <TableCell className="text-right text-xs">{f.totalPOs}</TableCell>
                    <TableCell className="text-right font-semibold">{fmtPct(f.otifPercent)}</TableCell>
                    <TableCell className="text-right text-xs">{fmtDias(f.leadTimeMedioDias)}</TableCell>
                    <TableCell className="text-right text-xs">{fmtPct(f.divergenciaCustoMediaPercent)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Badge variant="outline" className={classColor(f.classificacao)}>
                          {f.classificacao}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{f.score.toFixed(1)}</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── KpiCard ──────────────────────────────────────────────────────────────────

function KpiCard({
  icon, label, valor, subtitulo,
}: {
  icon:       React.ReactNode;
  label:      string;
  valor:      string;
  subtitulo?: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs text-muted-foreground flex items-center gap-2 font-normal">
          {icon} {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{valor}</div>
        {subtitulo && <p className="text-xs text-muted-foreground mt-1">{subtitulo}</p>}
      </CardContent>
    </Card>
  );
}
